import { createSlice } from "@reduxjs/toolkit";
import { getMedicalPaAnalytics } from "../../api/postCall/analyticsData";
import { MedicalPaAnalyticsPayload } from "../../data-model/analyticsDatamodel";
import { logError } from "../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { AppDispatch } from "../store/store";

interface AnalyticsDataState {
  analyticsData: any;
  isLoading: boolean;
  error: string | null;
}

const initialState: AnalyticsDataState = {
  analyticsData: null,
  isLoading: false,
  error: null,
};

const analyticsDataSlice = createSlice({
  name: "analyticsData",
  initialState,
  reducers: {
    setAnalyticsData: (state, action) => {
      state.analyticsData = action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    resetAnalyticsData: (state) => {
      state.analyticsData = null;
      state.isLoading = false;
      state.error = null;
    },
  },
});

export const fetchInitialAnalyticsData =
  () => async (dispatch: AppDispatch, getState: any) => {
    const { analyticsData } = getState();
    if (analyticsData.isLoading) {
      return;
    }

    dispatch(analyticsDataSlice.actions.setIsLoading(true));
    dispatch(analyticsDataSlice.actions.setError(null));

    try {
      const orgId = getOrgIdForFetchExternalWorklist();
      if (!orgId) {
        dispatch(
          analyticsDataSlice.actions.setError("Organization ID not available"),
        );
        return;
      }
      const payload = {
        org_id: orgId,
      };

      const response = await getMedicalPaAnalytics(payload);

      dispatch(analyticsDataSlice.actions.setAnalyticsData(response));
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Error fetching analytics data";
      dispatch(analyticsDataSlice.actions.setError(errorMessage));
      logError(error as Error, "Error fetching analytics data");
    } finally {
      dispatch(analyticsDataSlice.actions.setIsLoading(false));
    }
  };

export const fetchAnalyticsDataFromApi =
  (startDate?: Date, endDate?: Date) =>
  async (dispatch: AppDispatch, getState: any) => {
    const { analyticsData } = getState();
    if (analyticsData.isLoading) {
      return;
    }

    dispatch(analyticsDataSlice.actions.setIsLoading(true));
    dispatch(analyticsDataSlice.actions.setError(null));

    try {
      const orgId = getOrgIdForFetchExternalWorklist();

      if (!orgId) {
        dispatch(
          analyticsDataSlice.actions.setError("Organization ID not available"),
        );
        return;
      }
      const payload: MedicalPaAnalyticsPayload = {
        org_id: orgId,
        upload_date_from: startDate?.toLocaleDateString("en-CA") || undefined,
        upload_date_to: endDate?.toLocaleDateString("en-CA") || undefined,
      };

      const response = await getMedicalPaAnalytics(payload);
      dispatch(analyticsDataSlice.actions.setAnalyticsData(response));
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Error fetching analytics data";
      dispatch(analyticsDataSlice.actions.setError(errorMessage));
      logError(error as Error, "Error fetching analytics data");
    } finally {
      dispatch(analyticsDataSlice.actions.setIsLoading(false));
    }
  };

export const { setAnalyticsData, setIsLoading, setError, resetAnalyticsData } =
  analyticsDataSlice.actions;
export default analyticsDataSlice.reducer;
